/**
 * =============================================================================
 * Super A Sports — WooCommerce notice banners: real close button + auto-close
 * =============================================================================
 * Task 2 (Worker C). Locked behaviour, decided by the client:
 *
 *   • EVERY notice gets a working × button.
 *   • success / info notices auto-close after AUTO_CLOSE_MS (6s).
 *   • error / warning (and any status we cannot classify) NEVER auto-close —
 *     a shopper must not lose a checkout error.
 *
 * Measured facts this module is built on (probes 1-6, WooCommerce 10.6.2):
 *
 *  1. Two notice systems reach the front end:
 *     a) PHP / classic notices, printed inside `.woocommerce-notices-wrapper`
 *        or the `woocommerce/store-notices` block. Modern Woo renders them with
 *        the *block* classes, e.g. after add-to-cart:
 *          <div class="wc-block-components-notice-banner is-success"
 *               role="alert" tabindex="-1">           ← NO dismiss button
 *        and on a failed login:
 *          <div class="wc-block-components-notice-banner is-error" role="alert">
 *        These are plain server HTML: no entry in the notices store, so DOM
 *        removal is the correct (and only) way to dismiss them.
 *     b) React notices rendered by `StoreNoticesContainer` from the
 *        `core/notices` store, e.g.
 *          <div class="wc-block-store-notice wc-block-components-notice-banner
 *                      is-success is-dismissible">…<button class="…__dismiss">
 *        Woo only renders that native button when the notice was created with
 *        `isDismissible: true`; `isDismissible: false` notices (measured: the
 *        cart's own error notices) render with NO button at all.
 *
 *  2. THE PITFALL. A React notice that is only removed from the DOM stays in
 *     the `core/notices` store, so the next `StoreNoticesContainer` render puts
 *     it straight back on screen. Measured: after DOM-removing two `wc/cart`
 *     banners the store still reported 2 entries. So we dismiss through the
 *     store — `wp.data.dispatch('core/notices').removeNotice(id, context)` —
 *     matching the banner to its store entry by (tag-stripped, entity-decoded)
 *     text, and fall back to the native button, then to DOM removal, only when
 *     no store entry matches.
 *
 *  3. Notices arrive asynchronously (cart / checkout are Woo Blocks), so a
 *     one-shot querySelectorAll misses them: a MutationObserver on <body>
 *     drives everything and injection is idempotent (`data-sa-notice` flag).
 *
 * The CSS lives in src/scss/woo/_wc-notices.scss (notices also appear on
 * checkout, so they get their own partial rather than _cart.scss).
 */

var AUTO_CLOSE_MS = 6000; // success / info only
var FADE_MS = 220; // must stay <= the transition in _wc-notices.scss
var MIN_RESUME_MS = 600; // never re-arm with a sliver of time left
var LABEL = "Dismiss this notice";

// Every banner flavour we own. `.wc-block-components-notice-banner` covers both
// the block notices and the PHP notices (Woo prints classic notices with the
// block markup since 8.x); the three `.woocommerce-*` classes cover any
// remaining classic templates (e.g. plugin output, my-account forms).
var BANNER_SELECTOR = [
	".wc-block-components-notice-banner",
	".woocommerce-message",
	".woocommerce-error",
	".woocommerce-info",
].join(",");

// Woo's own dismiss button (block) and the classic one.
var NATIVE_DISMISS_SELECTOR =
	".wc-block-components-notice-banner__dismiss, .woocommerce-message-close";

var AUTO_CLOSE_STATUSES = ["success", "info"];

// Contexts WooCommerce registers notices under. `core/notices` has no public
// "list every context" selector, so this list is explicit; it is merged with
// `wc.wcBlocksData.noticeContexts` at runtime when that export exists.
var NOTICE_CONTEXTS = [
	"default",
	"wc/cart",
	"wc/checkout",
	"wc/cart-coupons",
	"wc/checkout-coupons",
	"wc/express-payment",
	"wc/checkout/shipping-address",
	"wc/checkout/billing-address",
	"wc/checkout/contact-information",
	"wc/checkout/shipping-methods",
	"wc/checkout/payment-method",
	"wc/checkout/additional-information",
	"wc/checkout/order-notes",
	"wc/checkout/terms",
	"wc/mini-cart",
	"wc/single-product",
	"wc/all-products",
];

var CLOSE_ICON =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">' +
	'<path d="M13 11.8l6.1-6.3-1-1-6.1 6.2-6.1-6.2-1 1 6.1 6.3-6.5 6.7 1 1 6.5-6.6 6.5 6.6 1-1z"></path></svg>';

// Auto-close bookkeeping. WeakMap so a removed banner cannot leak a timer.
var timers = new WeakMap();

// Last dismissal, surfaced on window.saNotices.last so QA can assert WHICH
// path ran (store / native / dom) after the node itself has gone.
var lastDismissal = null;

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------
function prefersReducedMotion() {
	return (
		typeof window.matchMedia === "function" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	);
}

function normalise(value) {
	return String(value == null ? "" : value)
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();
}

/** Store notice content is HTML-ish ("Coupon &quot;x&quot; …") — decode it. */
function htmlToText(html) {
	if (html == null) {
		return "";
	}
	if (typeof html !== "string") {
		// React element / object content: not comparable, force the fallback path.
		return "";
	}
	var holder = document.createElement("div");
	holder.innerHTML = html;
	return normalise(holder.textContent);
}

/** The banner's own message, with our injected button excluded. */
function bannerText(banner) {
	var source =
		banner.querySelector(".wc-block-components-notice-banner__content") ||
		banner;
	var clone = source.cloneNode(true);
	Array.prototype.slice
		.call(clone.querySelectorAll("[data-sa-notice-close]"))
		.forEach(function (node) {
			node.parentNode.removeChild(node);
		});
	return normalise(clone.textContent);
}

function noticesStore() {
	try {
		if (!window.wp || !window.wp.data || !window.wp.data.select) {
			return null;
		}
		var select = window.wp.data.select("core/notices");
		return select && typeof select.getNotices === "function" ? select : null;
	} catch (error) {
		return null;
	}
}

function noticeContexts() {
	var list = NOTICE_CONTEXTS.slice();
	try {
		var extra =
			window.wc && window.wc.wcBlocksData && window.wc.wcBlocksData.noticeContexts;
		if (extra) {
			Object.keys(extra).forEach(function (key) {
				if (typeof extra[key] === "string" && list.indexOf(extra[key]) === -1) {
					list.push(extra[key]);
				}
			});
		}
	} catch (error) {
		/* noticeContexts is not part of the public API — ignore when absent */
	}
	return list;
}

/**
 * Find the `core/notices` entry a banner was rendered from, so it can be
 * dismissed at the source instead of being torn out of the DOM.
 * Returns { id, context } or null.
 */
function findStoreNotice(banner) {
	var select = noticesStore();
	if (!select) {
		return null;
	}

	var text = bannerText(banner);
	if (!text) {
		return null;
	}

	var contexts = noticeContexts();
	var loose = null;

	for (var i = 0; i < contexts.length; i++) {
		var list;
		try {
			list = select.getNotices(contexts[i]) || [];
		} catch (error) {
			continue;
		}
		for (var j = 0; j < list.length; j++) {
			var notice = list[j];
			var content =
				htmlToText(notice.content) || htmlToText(notice.__unstableHTML);
			if (!content) {
				continue;
			}
			if (content === text) {
				return { id: notice.id, context: contexts[i] };
			}
			// Woo sometimes wraps the message with extra markup ("… View cart").
			if (!loose && content.length > 8 && text.indexOf(content) !== -1) {
				loose = { id: notice.id, context: contexts[i] };
			}
		}
	}

	return loose;
}

function removeFromStore(match) {
	try {
		window.wp.data.dispatch("core/notices").removeNotice(match.id, match.context);
		return true;
	} catch (error) {
		return false;
	}
}

// -----------------------------------------------------------------------------
// Status — read from the live DOM, never guessed from the trigger
// -----------------------------------------------------------------------------
function statusOf(banner) {
	var cl = banner.classList;
	if (cl.contains("is-error") || cl.contains("woocommerce-error")) {
		return "error";
	}
	if (cl.contains("is-warning")) {
		return "warning";
	}
	if (cl.contains("is-success") || cl.contains("woocommerce-message")) {
		return "success";
	}
	if (cl.contains("is-info") || cl.contains("woocommerce-info")) {
		return "info";
	}
	// No status class: treat as persistent. Losing an unclassified message is
	// worse than leaving one banner on screen until the shopper closes it.
	return "unknown";
}

// -----------------------------------------------------------------------------
// Auto-close timer, pausable while the shopper is reading
// -----------------------------------------------------------------------------
function clearTimer(banner) {
	var state = timers.get(banner);
	if (state && state.id) {
		window.clearTimeout(state.id);
		state.id = 0;
	}
}

function startTimer(banner) {
	var state = timers.get(banner);
	if (!state || state.id || state.done) {
		return;
	}
	state.startedAt = Date.now();
	state.id = window.setTimeout(function () {
		state.id = 0;
		state.done = true;
		dismiss(banner, "auto");
	}, state.remaining);
}

function pauseTimer(banner) {
	var state = timers.get(banner);
	if (!state || !state.id) {
		return;
	}
	window.clearTimeout(state.id);
	state.id = 0;
	var left = state.remaining - (Date.now() - state.startedAt);
	state.remaining = Math.max(MIN_RESUME_MS, left);
}

function armAutoClose(banner) {
	timers.set(banner, { id: 0, remaining: AUTO_CLOSE_MS, startedAt: 0, done: false });
	startTimer(banner);

	// Politeness: never vanish under the pointer or while focus is inside.
	banner.addEventListener("mouseenter", function () {
		pauseTimer(banner);
	});
	banner.addEventListener("mouseleave", function () {
		startTimer(banner);
	});
	banner.addEventListener("focusin", function (event) {
		// WooCommerce renders success banners with `tabindex="-1"` and focuses
		// the WRAPPER on load so screen readers announce it. Measured: that
		// programmatic focus never leaves, so pausing on it kept the notice on
		// screen for ever (13s+, auto-close never fired). Only a focused CHILD
		// — the × button, a "View cart" link — means the shopper is interacting.
		if (event.target === banner) {
			return;
		}
		pauseTimer(banner);
	});
	banner.addEventListener("focusout", function (event) {
		if (!banner.contains(event.relatedTarget)) {
			startTimer(banner);
		}
	});
}

// -----------------------------------------------------------------------------
// Dismissal — store first, native button second, DOM last
// -----------------------------------------------------------------------------
function dismiss(banner, reason) {
	if (banner.getAttribute("data-sa-notice-closing") === "1") {
		return;
	}
	banner.setAttribute("data-sa-notice-closing", "1");
	banner.setAttribute("data-sa-notice-reason", reason || "click");
	clearTimer(banner);

	var finish = function () {
		var handled = false;

		var match = findStoreNotice(banner);
		if (match) {
			handled = removeFromStore(match);
			banner.setAttribute(
				"data-sa-notice-dismissed-by",
				handled ? "store:" + match.context + "/" + match.id : "store-failed"
			);
		}

		if (!handled) {
			// Woo's own button already dispatches removeNotice() for React
			// notices, so use it before touching the DOM.
			var native = banner.querySelector(NATIVE_DISMISS_SELECTOR);
			if (native && !native.hasAttribute("data-sa-notice-close")) {
				handled = true;
				banner.setAttribute("data-sa-notice-dismissed-by", "native");
				native.click();
			}
		}

		if (!handled) {
			banner.setAttribute("data-sa-notice-dismissed-by", "dom");
		}

		lastDismissal = {
			by: banner.getAttribute("data-sa-notice-dismissed-by"),
			status: banner.getAttribute("data-sa-notice-status"),
			reason: reason || "click",
			text: bannerText(banner).slice(0, 80),
			at: Date.now(),
		};
		if (window.saNotices) {
			window.saNotices.last = lastDismissal;
		}

		// PHP notices have no store entry, so the DOM is the only handle: drop
		// them straight away. When the store DID own the notice, React unmounts
		// the node itself — tearing it out from under React logs
		// "NotFoundError: removeChild … not a child of this node" and leaves the
		// notices container broken, so only sweep up much later and only if
		// React left the node behind.
		window.setTimeout(
			function () {
				if (banner.isConnected && banner.parentNode) {
					banner.parentNode.removeChild(banner);
				}
			},
			handled ? 400 : 80
		);
	};

	if (prefersReducedMotion()) {
		finish();
		return;
	}

	banner.classList.add("sa-notice--closing");
	window.setTimeout(finish, FADE_MS);
}

// -----------------------------------------------------------------------------
// Button injection
// -----------------------------------------------------------------------------
function makeCloseButton(banner) {
	var button = document.createElement("button");
	button.type = "button";
	button.className = "sa-notice__dismiss";
	button.setAttribute("aria-label", LABEL);
	button.setAttribute("data-sa-notice-close", "");
	button.innerHTML = CLOSE_ICON;
	// A real <button> already fires click on Enter *and* Space — no key handler.
	button.addEventListener("click", function (event) {
		event.preventDefault();
		event.stopPropagation();
		dismiss(banner, "click");
	});
	return button;
}

function enhance(banner) {
	if (!banner || banner.getAttribute("data-sa-notice") === "1") {
		return; // idempotent: never a second × on the same banner
	}
	// Inline field errors are not notices — leave them alone.
	if (banner.classList.contains("wc-block-components-validation-error")) {
		return;
	}
	banner.setAttribute("data-sa-notice", "1");

	var status = statusOf(banner);
	banner.setAttribute("data-sa-notice-status", status);

	var native = banner.querySelector(NATIVE_DISMISS_SELECTOR);
	if (native) {
		// Requirement 4: keep Woo's own button (React owns its handler, which
		// already removes the notice from the store) — only make sure it is
		// labelled and that our auto-close timer stands down when it is used.
		if (!native.getAttribute("aria-label")) {
			native.setAttribute("aria-label", LABEL);
		}
		native.addEventListener("click", function () {
			clearTimer(banner);
			banner.setAttribute("data-sa-notice-closing", "1");
		});
	} else {
		banner.appendChild(makeCloseButton(banner));
	}
	banner.setAttribute("data-sa-notice-dismissible", "1");

	if (AUTO_CLOSE_STATUSES.indexOf(status) !== -1) {
		armAutoClose(banner);
	} else {
		banner.setAttribute("data-sa-notice-persist", "1");
	}
}

function scan(root) {
	var scope = root && root.querySelectorAll ? root : document;
	if (scope.matches && scope.matches(BANNER_SELECTOR)) {
		enhance(scope);
	}
	Array.prototype.slice
		.call(scope.querySelectorAll(BANNER_SELECTOR))
		.forEach(enhance);
}

// -----------------------------------------------------------------------------
// Boot — MutationObserver, because React injects notices long after load
// -----------------------------------------------------------------------------
export function initWcNotices() {
	if (!document.body) {
		return;
	}

	scan(document);

	var observer = new MutationObserver(function (records) {
		for (var i = 0; i < records.length; i++) {
			var added = records[i].addedNodes;
			for (var j = 0; j < added.length; j++) {
				var node = added[j];
				if (node.nodeType !== 1) {
					continue;
				}
				scan(node);
			}
		}
	});

	// Injecting the button is itself a childList mutation, but `enhance()` is
	// flag-guarded, so the re-scan it triggers is a no-op and cannot loop.
	observer.observe(document.body, { childList: true, subtree: true });

	// Late stylesheet/hydration passes can swap a banner in during `load`.
	window.addEventListener("load", function () {
		scan(document);
	});

	// Test hook (also handy in the console): saNotices.scan() / .dismissAll().
	window.saNotices = {
		scan: function () {
			scan(document);
		},
		dismiss: dismiss,
		autoCloseMs: AUTO_CLOSE_MS,
		last: lastDismissal,
	};
}
